import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";

import { OnboardingAgentSendPlaneIcon } from "../OnboardingAgentSendPlaneIcon/OnboardingAgentSendPlaneIcon";
import {
    ONBOARDING_AGENT_INPUT_PLACEHOLDER,
    ONBOARDING_AGENT_INPUT_SEND_ARIA,
    ONBOARDING_AGENT_INPUT_STOP_ARIA,
} from "../texts";

import { COMPOSER_TEXTAREA_ROWS } from "./constants";
import { InputShell, SendButton, StopIcon, StyledTextarea, TextAreaColumn } from "./styles";
import { useOnboardingAgentComposer } from "./hooks/useOnboardingAgentComposer";

type OnboardingAgentComposerProps = {
    onSend?: (message: string) => void;
    onStop?: () => void;
    isDisabled?: boolean;
    isLoading?: boolean;
    focusTrigger?: number;
};

export const OnboardingAgentComposer = ({
    onSend,
    onStop,
    isDisabled = false,
    isLoading = false,
    focusTrigger = 0,
}: OnboardingAgentComposerProps) => {
    const { translate } = useTranslate();
    const {
        value,
        textareaRef,
        canSend,
        isActivated,
        handleActionButtonClick,
        handleInputChange,
        handleKeyDown,
        handleFocus,
        handleBlur,
    } = useOnboardingAgentComposer({ onSend, onStop, isDisabled, isLoading, focusTrigger });

    return (
        <InputShell
            $activated={isActivated}
            $isLoading={isLoading}
            data-automation="onboarding-agent-composer"
        >
            <TextAreaColumn>
                <StyledTextarea
                    ref={textareaRef}
                    rows={COMPOSER_TEXTAREA_ROWS}
                    autoFocus
                    disabled={isDisabled}
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={translate(ONBOARDING_AGENT_INPUT_PLACEHOLDER)}
                    aria-label={translate(ONBOARDING_AGENT_INPUT_PLACEHOLDER)}
                    data-automation="onboarding-agent-composer-input"
                />
            </TextAreaColumn>
            <SendButton
                type="button"
                $canSend={canSend}
                $isLoading={isLoading}
                disabled={!canSend ? !isLoading : undefined}
                onClick={handleActionButtonClick}
                aria-label={translate(isLoading ? ONBOARDING_AGENT_INPUT_STOP_ARIA : ONBOARDING_AGENT_INPUT_SEND_ARIA)}
                data-automation={isLoading ? "onboarding-agent-composer-stop" : "onboarding-agent-composer-send"}
            >
                {isLoading ? <StopIcon /> : <OnboardingAgentSendPlaneIcon fill={colorsPalettes.carbon[0]} />}
            </SendButton>
        </InputShell>
    );
};
